<template>
    <div class="chat-container" v-show="showChat">
         <!-- Barra de Título do Chat -->
        <div class="chat-title">
            <span class="status-dot" :class="{'is-synchronized': isSynchronized, 'not-synchronized': !isSynchronized}"></span>
            Visualizador
            <div class="synchronizing-text">{{ isSynchronized ? 'Sincronizado' : 'Sem conexão com o servidor CSML' }}</div>
        </div>

        <div class="messages-container" ref="messages">
            <div v-for="(msg, index) in messages" :key="index" :class="['message', {'message-user': msg.sender === 'user', 'message-other': msg.sender === 'other'}]">
                <!-- Texto Simples -->
                <div v-if="msg.type === 'text'" class="message-content">{{ msg.content }}</div>

                <!-- Mensagem de "Digitando..." -->
                <div v-else-if="msg.type === 'typing'" class="message-content typing-animation">
                    <span>●</span><span>●</span><span>●</span>
                </div>

                <!-- Questão -->
                <div v-else-if="msg.type === 'question'" class="message-content">
                    <p>{{ msg.content.title }}</p>
                    <div>
                        <button
                        v-for="(option, optionIndex) in msg.content.buttons"
                        :key="optionIndex"
                        class="question-button"
                        @click="handleQuestionClick(option)">
                        {{ option.content.title }}
                        </button>
                    </div>
                </div>

                <!-- Imagem -->
                <div v-else-if="msg.type === 'image'" class="message-content">
                    <img :src="msg.content.url" :alt="msg.content.alt || 'Imagem'">
                </div>

                <!-- URL -->
                <div v-else-if="msg.type === 'url'" class="message-content">
                    <a :href="msg.content.url" target="_blank" rel="noopener">{{ msg.content.text || msg.content.title || msg.content.url }}</a>
                </div>

                <div class="message-timestamp">{{ msg.timestamp }}</div>
            </div>
        </div>

        <div class="message-input-container">
            <input type="text" v-model="newMessage" @keydown.enter.prevent="sendMessage" placeholder="Digite sua mensagem aqui..." />
            <button @click="sendMessage">Enviar</button>
        </div>

        <!-- Seção de Debug -->
        <div class="debug-links">
            Debug:
            <div @click="newConversation()">Nova conversa</div>
            <div @click="resetMemory">Limpar Memória</div>
            <div @click="showMemorydata">Memória</div>
            <div @click="showSessionData">Dados da Sessão</div>
            <div @click="showMetadata">Metadata</div>
        </div>
    </div>
  </template>

  <script>
    import CsmlEngineApi from '@/services/CsmlEngineApi';
    import { EventBus } from '../event-bus.js';
    import { escapeHtml, describeError } from '../utils.js';

    function now() {
        const date = new Date();
        return date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
    }

    function log(message) {
        EventBus.$emit('add-console-message', message);
    }

  export default {
    props: ['showChat','csmlContent', 'sessionData', 'metaData'],
    data() {
        return {
            isSynchronized: true,
            newMessage: '',
            messages: [],
            sending: false,
        };
    },
    methods: {
        addMessage(message) {
            this.messages.push({ timestamp: now(), ...message });
            this.scrollToEnd();
        },
        sendMessage() {
            const text = this.newMessage.trim();
            if (text === '') return;
            this.addMessage({ type: 'text', content: text, sender: 'user' });
            this.newMessage = '';
            this.sendBotMessage(text);
        },
        async sendBotMessage(mensagem) {
            if (!this.csmlContent.trim()) {
                log('<span style="color: red;">O editor está vazio: escreva ou abra um bot antes de conversar.</span>');
                return;
            }
            let response;
            try {
                response = await CsmlEngineApi.runBot(this.sessionData, {}, this.metaData, this.csmlContent, mensagem);
                this.isSynchronized = true;
            } catch (error) {
                this.isSynchronized = !(error.response && error.response.status >= 502);
                log(`<span style="color: red;">Erro ao rodar o bot: ${describeError(error)}</span>`);
                return;
            }

            for (const mensagemBot of response.data.messages || []) {
                const payload = mensagemBot.payload;
                const content = payload.content;

                switch (payload.content_type) {
                    case 'text':
                    case 'question':
                    case 'image':
                    case 'url':
                        this.addMessage({ type: payload.content_type, content: payload.content_type === 'text' ? content.text : content, sender: 'other' });
                        break;
                    case 'error':
                        // No MA o cliente não vê esses erros: ficam só no terminal
                        log(`<span style="color: red;">Erro do bot: ${escapeHtml(content.error)}</span>`);
                        break;
                    case 'wait':
                        log(`Recebido comando de Espera [${content.duration}ms]`);
                        await new Promise(resolve => setTimeout(resolve, content.duration));
                        break;
                    case 'typing': {
                        log(`Recebido comando de Digitando... [${content.duration}ms]`);
                        const typingMessage = { type: 'typing', content: '...', sender: 'other', timestamp: now() };
                        this.messages.push(typingMessage);
                        this.scrollToEnd();
                        await new Promise(resolve => setTimeout(resolve, content.duration));
                        const index = this.messages.indexOf(typingMessage);
                        if (index !== -1) {
                            this.messages.splice(index, 1);
                        }
                        break;
                    }
                    default:
                        // O MA não entrega esses tipos ao WhatsApp; mostra o conteúdo cru
                        this.addMessage({ type: 'text', content: `[${payload.content_type} não suportado no MA] ${JSON.stringify(content)}`, sender: 'other' });
                        log(`Mensagem do tipo <b>${escapeHtml(payload.content_type)}</b> não é suportada pelo MA.`);
                }
            }

            if (response.data.conversation_end) {
                log('<span style="color: yellow;">Conversa encerrada pelo bot (goto end). A próxima mensagem começa do início.</span>');
            }
        },
        handleQuestionClick(option) {
            this.addMessage({ type: 'text', content: option.content.title, sender: 'user' });
            // No WhatsApp a resposta do botão chega como o texto do título
            this.sendBotMessage(option.content.title);
        },
        async newConversation(silent) {
            try {
                await CsmlEngineApi.closeConversation(this.sessionData);
            } catch (error) {
                log(`<span style="color: red;">Erro ao encerrar a conversa: ${describeError(error)}</span>`);
                return;
            }
            this.messages = [];
            if (!silent) {
                log('<span style="color: green;">Nova conversa: a próxima mensagem começa do início do bot.</span>');
            } else {
                log('Conversa reiniciada para o código novo.');
            }
        },
        async resetMemory() {
            try {
                await CsmlEngineApi.memoriesBotDelete(this.sessionData);
                log('<span style="color: green;">Dados da Memória Apagados!</span>');
            } catch (error) {
                log(`<span style="color: red;">Erro ao apagar a memória: ${describeError(error)}</span>`);
            }
        },
        showSessionData() {
            log('<span style="color: yellow;">Dados da Sessão:</span>');
            Object.entries(this.sessionData).forEach(([key, value]) => {
                log(`${key}: ${escapeHtml(value)}`);
            });
        },
        showMetadata() {
            log('<span style="color: yellow;">Dados do MetaData:</span>');
            log(escapeHtml(JSON.stringify(this.metaData, null, 2)));
        },
        async showMemorydata() {
            let response;
            try {
                response = await CsmlEngineApi.memoriesBot(this.sessionData);
            } catch (error) {
                log(`<span style="color: red;">Erro ao ler a memória: ${describeError(error)}</span>`);
                return;
            }
            log('<span style="color: yellow;">Dados da Memória:</span>');
            if (!response.data.length) {
                log('(vazia)');
            }
            response.data.forEach(mem => {
                log(`Key[${escapeHtml(mem.key)}] = ${escapeHtml(JSON.stringify(mem.value))} -> ${escapeHtml(mem.created_at)}`);
            });
        },
        onChatAction(action) {
            const actions = {
                'nova-conversa': () => this.newConversation(true),
                'memoria': this.showMemorydata,
                'limpar-memoria': this.resetMemory,
                'sessao': this.showSessionData,
            };
            if (actions[action]) actions[action]();
        },
        scrollToEnd() {
            this.$nextTick(() => {
                const container = this.$refs.messages;
                if (container) container.scrollTop = container.scrollHeight;
            });
        }
    },
    created() {
        EventBus.$on('chat-action', this.onChatAction);
    },
    beforeDestroy() {
        EventBus.$off('chat-action', this.onChatAction);
    }
  }
  </script>

<style scoped>
   .chat-container {
        /* Ajustes adicionais para o chat conforme necessário */
        display: flex;
        flex-direction: column;
        height: 100%; /* Defina uma altura específica ou faça com que ocupe todo o espaço disponível */
        max-width: 400px; /* Exemplo de largura fixa */
        width: 100%; /* Garante que o container use a largura máxima até 400px */
        margin: 0 auto; /* Centraliza o chat-container se estiver em um viewport maior */
        overflow: hidden; /* Previne que o conteúdo interno faça o container expandir lateralmente */
      }

    .message {
        display: flex;
        flex-direction: column;
        align-items: flex-start; /* Alinha o conteúdo à esquerda para 'message-other' */
        margin-bottom: 10px;
    }

    .message-user {
        align-items: flex-end; /* Alinha o conteúdo à direita para 'message-user' */
    }

    .message-content {
        max-width: 80%;
        padding: 8px;
        margin-bottom: 4px; /* Espaço entre a mensagem e o timestamp */
        border-radius: 4px;
        word-wrap: break-word; /* Quebra palavras para evitar overflow horizontal */
        white-space: pre-wrap; /* Respeita as quebras de linha e espaços em branco */

    }

    .message-timestamp {
        font-size: 0.75rem;
        color: #888;
        align-self: flex-end; /* Garante que o timestamp fique alinhado à direita do conteúdo da mensagem */
    }

    .message-other .message-timestamp {
        align-self: flex-start; /* Para mensagens de 'outro', alinha o timestamp à esquerda */
    }

    /* Estilos para a mensagem do usuário */
    .message-user .message-content {
        background-color: #406efb; /* Cor de fundo clara para mensagens enviadas */
        color: #ffffff; /* Cor do texto */
        margin-right: 10px; /* Espaço para alinhar as mensagens à direita */
        border-radius: 12px 0 12px 12px; /* Borda arredondada específica */
    }

    /* Estilos para a mensagem do outro */
    .message-other .message-content {
        background-color: #f0f2f6; /* Cor de fundo diferente para mensagens recebidas */
        color: #1c3c8e; /* Cor do texto */
        margin-left: 10px; /* Espaço para alinhar as mensagens à esquerda */
        border-radius: 0 12px 12px 12px; /* Borda arredondada específica */
    }
    .messages-container {
        flex-grow: 1;
        overflow-y: auto; /* Permite rolagem vertical se o conteúdo exceder a altura do contêiner */
        padding: 10px;
        margin: 8px 0; /* Ajuste conforme necessário para margens internas */
        max-height: calc(100vh - 100px); /* Ajuste este valor conforme necessário */
        /* A altura máxima é calculada com base na altura total da viewport (100vh) menos o espaço ocupado por outros elementos */
        background-color: #ffffff;
    }

    .message-input-container {
        display: flex;
        padding: 10px;
        background-color: #dadada;
        border-top: 1px solid #afafaf;
    }

    .message-input-container input[type="text"] {
        flex-grow: 1;
        margin-right: 10px;
        padding: 10px;
        border: 1px solid #ccc;
        border-radius: 100px;
    }

    .message-input-container button {
        padding: 10px 15px;
        background-color: #007bff;
        color: white;
        border: none;
        border-radius: 30px;
        cursor: pointer;
        margin: 1px; /* Adiciona espaço ao redor do botão */
    }

    .message-input-container button:hover {
        background-color: #0056b3;
    }

    .chat-title {
        background-color: #f0f0f0; /* Cinza um pouco mais escuro que o branco */
        color: black; /* Cor do texto */
        padding: 10px; /* Espaçamento interno */
        text-align: left; /* Alinhamento do texto */
        font-weight: bold; /* Peso da fonte */
        box-shadow: 0 2px 4px rgba(0,0,0,0.1); /* Sombra abaixo da barra de título */
        position: relative; /* Necessário para a sobreposição correta da sombra */
        z-index: 1; /* Garante que a sombra apareça sobre os elementos abaixo */
    }

    .status-dot {
        height: 10px;
        width: 10px;
        border-radius: 50%;
        display: inline-block;
        margin-right: 5px;
    }

    .is-synchronized {
        background-color: #28a745; /* Verde */
    }

    .not-synchronized {
        background-color: #dc3545; /* Vermelho */
    }

    .synchronizing-text {
        font-size: 1rem; /* Fonte menor */
        color: #6c757d; /* Cinza claro */
        display: block; /* Faz com que o texto "Sincronizando..." seja exibido em uma nova linha */
    }

    .debug-links {
        display: flex;
        justify-content: space-around; /* Distribui espaço uniformemente entre os itens */
        padding: 10px 0; /* Adiciona um pouco de padding vertical para separação */
        font-size: 1rem; /* Tamanho menor de fonte */
        background: #111827;
        color:#ffffff;
    }

    .debug-links div {
        cursor: pointer;
        color: #007bff;
        transition: background-color 0.3s; /* Suaviza a mudança de cor de fundo */
    }

    .debug-links div:hover {
        color: #ace216;
    }

    .question-button {
        margin: 5px;
        padding: 10px 15px;
        background-color: #007bff;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
    }

    .question-button:hover {
        background-color: #0056b3;
    }


    .message-content a {
        color: inherit;
        text-decoration: underline;
        word-break: break-all;
    }

    .message-content img {
        max-width: 100%;
        border-radius: 8px;
    }

    .typing-animation span {
        display: inline-block;
        animation: bounce 1.4s infinite;
        font-size: 24px; /* Aumenta o tamanho da fonte */
        margin: 0 2px; /* Adiciona espaço entre os pontos */
    }

    .typing-animation span:nth-child(1) {
        animation-delay: 0s;
    }

    .typing-animation span:nth-child(2) {
        animation-delay: 0.2s;
    }

    .typing-animation span:nth-child(3) {
        animation-delay: 0.4s;
    }

    @keyframes bounce {
        0%, 80%, 100% {
            transform: translateY(0);
        }
        40% {
            transform: translateY(-10px);
        }
    }
</style>